/**
 *
 * App.js
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 *
 */

import React from 'react';
import { Navigate, Route, Routes, Outlet } from 'react-router-dom';
import loadable from '../utils/loadable';
import { Routes as AppRoutes } from '../utils/constants/routes';

import GlobalStyle from '../global-styles';
// page imports
import DefaultLayout from '../layout/index';
import useUser from '../components/useUser';
import { createLogger } from '../utils/logger';
import ErrorBoundaryBadEntityId from '../error-boundaries/ErrorBoundaryBadEntityId';
import { InventoryLayout } from '../layout/InventoryLayout';
import { PeopleLayout } from '../layout/PeopleLayout';

const log = createLogger('routes:::');

const Dashboard = loadable(() => import('./home'));
const Canvas = loadable(() => import('./dashboard'));

const InventorySummary = loadable(() => import('./inventory/summary'));
const InventoryApplications = loadable(() => import('./inventory/applications'));
const InventoryComponents = loadable(() => import('./inventory/components'));
const InventoryPortfolios = loadable(() => import('./inventory/portfolios'));
const InventoryBusinesses = loadable(() => import('./inventory/businesses'));
const InventoryTechnologies = loadable(() => import('./inventory/technologies'));
const InventoryServices = loadable(() => import('./inventory/services'));
const InventoryCapabilities = loadable(() => import('./inventory/capabilities'));
const InventoryVendors = loadable(() => import('./inventory/vendors'));

const ApplicationItem = loadable(() => import('./inventory/application-item'));
const ComponentItem = loadable(() => import('./inventory/components-item'));
const PortfoliosItem = loadable(() => import('./inventory/portfolios-item'));
const BusinessesItem = loadable(() => import('./inventory/businesses-item'));
const CapabilitiesItem = loadable(() => import('./inventory/capabilities-item'));
const TechnologyItem = loadable(() => import('./inventory/technology-item'));
const ServiceItem = loadable(() => import('./inventory/service-item'));
const VendorItem = loadable(() => import('./inventory/vendor-item'));
const Settings = loadable(() => import('./settings'));
const IntegrationItem = loadable(() => import('./settings/integration-item'));
const Profile = loadable(() => import('./profile'));
const Landing = loadable(() => import('./landing'));
const ForgotPassword = loadable(() => import('./forgot-password'));

const PeopleRoster = loadable(() => import('./people/roster'));
const PeopleTeams = loadable(() => import('./people/teams'));
const PeopleRoles = loadable(() => import('./people/roles'));
const PeopleLocations = loadable(() => import('./people/locations'));
const SinglePersonView = loadable(() => import('./people/single-person-view'));
const SingleTeamView = loadable(() => import('./people/single-team-view'));
const SingleRoleView = loadable(() => import('./people/single-role-view'));

const Explore = loadable(() => import('./explore'));
const ExploreCapabilities = loadable(() => import('./explore/capabilities'));
const TeamsExplorer = loadable(() => import('./explore/teams'));
const DataFlow = loadable(() => import('./explore/data-flow'));

const PrivateRoute = () => {
  const { user } = useUser();

  if (!user.isLoggedIn) {
    return <Navigate to={AppRoutes.LANDING} replace />;
  }

  return <Outlet />;
};

const Logout = () => {
  const userComponent = useUser();
  log.info('logout route fired.');
  userComponent.handlers.onLogout();

  return <div>...</div>;
};

function Router() {
  return (
    navigator.onLine && (
      <ErrorBoundaryBadEntityId>
        <Routes>
          <Route path={AppRoutes.LANDING} element={<Landing />} />
          <Route path={AppRoutes.FORGOT_PASSWORD} element={<ForgotPassword />} />
          <Route path={AppRoutes.LOGOUT} element={<Logout />} />

          <Route element={<PrivateRoute />}>
            <Route element={<DefaultLayout />}>
              <Route path={AppRoutes.ROOT} element={<Dashboard />} />
              <Route path={AppRoutes.EXPLORE} element={<Explore />} />
              <Route path={AppRoutes.EXPLORE_CAPABILITIES} element={<ExploreCapabilities />} />
              <Route path={AppRoutes.EXPLORE_TEAMS} element={<TeamsExplorer />} />
              <Route path={AppRoutes.CANVAS} element={<Canvas />} />
              <Route path={AppRoutes.DATA_FLOW} element={<DataFlow />} />
              <Route path={AppRoutes.SINGLE_BUSINESS} element={<BusinessesItem />} />
              <Route path={AppRoutes.SINGLE_APPLICATION} element={<ApplicationItem />} />
              <Route path={AppRoutes.SINGLE_PORTFOLIO} element={<PortfoliosItem />} />
              <Route path={AppRoutes.SINGLE_COMPONENT} element={<ComponentItem />} />
              <Route path={AppRoutes.SINGLE_CAPABILITY} element={<CapabilitiesItem />} />
              <Route path={AppRoutes.SINGLE_TECHNOLOGY} element={<TechnologyItem />} />
              <Route path={AppRoutes.SINGLE_SERVICE} element={<ServiceItem />} />
              <Route path={AppRoutes.SINGLE_VENDOR} element={<VendorItem />} />
              <Route path={AppRoutes.SETTINGS_SINGLE_INTEGRATION} element={<IntegrationItem />} />
              <Route path={`${AppRoutes.SETTINGS}/*`} element={<Settings />} />
              <Route path={AppRoutes.PROFILE} element={<Profile />} />
              <Route path={AppRoutes.SINGLE_PERSON} element={<SinglePersonView />} />
              <Route path={`${AppRoutes.SINGLE_TEAM}/*`} element={<SingleTeamView />} />
              <Route path={AppRoutes.SINGLE_ROLE} element={<SingleRoleView />} />
              {/* <Route path={AppRoutes.REPORTING_EXPLORE} component={ReportingExplore} />
              <Route path={AppRoutes.REPORTING_DASHBOARD} component={ReportingDashboard} /> */}
            </Route>

            <Route element={<InventoryLayout />}>
              <Route path={AppRoutes.INVENTORY} element={<InventorySummary />} />
              <Route path={AppRoutes.INVENTORY_PORTFOLIOS} element={<InventoryPortfolios />} />
              <Route path={AppRoutes.INVENTORY_APPLICATIONS} element={<InventoryApplications />} />
              <Route path={AppRoutes.INVENTORY_COMPONENTS} element={<InventoryComponents />} />
              <Route path={AppRoutes.INVENTORY_CAPABILITIES} element={<InventoryCapabilities />} />
              <Route path={AppRoutes.INVENTORY_BUSINESSES} element={<InventoryBusinesses />} />
              <Route path={AppRoutes.INVENTORY_TECHNOLOGIES} element={<InventoryTechnologies />} />
              <Route path={AppRoutes.INVENTORY_SERVICES} element={<InventoryServices />} />
              <Route path={AppRoutes.INVENTORY_VENDORS} element={<InventoryVendors />} />
            </Route>

            <Route element={<PeopleLayout />}>
              <Route path={AppRoutes.PEOPLE} element={<PeopleRoster />} />
              <Route path={AppRoutes.TEAMS} element={<PeopleTeams />} />
              <Route path={AppRoutes.ROLES} element={<PeopleRoles />} />
              <Route path={AppRoutes.LOCATIONS} element={<PeopleLocations />} />
            </Route>

            <Route path="*" element={<Navigate to={AppRoutes.ROOT} />} />
          </Route>
        </Routes>
        <GlobalStyle />
      </ErrorBoundaryBadEntityId>
    )
  );
}

export default Router;
